// Persistent Chats store — keeps the ONE Chat module instance (which owns the authoritative
// call state + the LiveKit CallSession) alive while a call/huddle is in progress, so that
// navigating away from /workspace/chats never unmounts it and never drops the call.
//
// How it works (no second calling implementation, no state duplication):
//   • PersistentChatsHost (App level, outside the route tree) renders WorkspaceChats through a
//     React portal into a single, stable container element owned by this store.
//   • While the Chats route is on screen, ChatsRouteSlot registers its DOM node and the store
//     MOVES the container into it — the chat UI renders exactly where the route outlet is.
//   • When the Chats route unmounts, the container is moved (synchronously, same task) into a
//     hidden "parking" node on <body>. React never sees a tree change, so WorkspaceChats keeps
//     its state, sockets, timers and media elements. Only when no call is active does the host
//     unmount it (identical to the previous lifecycle).
//
// Moving a portal container between DOM parents is safe: React portals listen for events on
// the container itself, and media elements that are re-inserted in the same task keep playing.

import { createContext, useContext, useSyncExternalStore } from "react";

let slotEl = null;
let keepAlive = false;
let badgeValue = null;
let container = null;
let parking = null;

const listeners = new Set();
let snapshot = { slotEl: null, keepAlive: false, badgeValue: null };

function emit() {
  snapshot = { slotEl, keepAlive, badgeValue };
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a listener must never break the others */
    }
  });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getSnapshot() {
  return snapshot;
}

/** The stable portal container WorkspaceChats is rendered into (created once). */
export function getHostContainer() {
  if (container || typeof document === "undefined") return container;
  container = document.createElement("div");
  container.setAttribute("data-persistent-chats", "");
  // `display: contents` keeps the chat UI a direct flex child of the route outlet, exactly as
  // it was when the route rendered WorkspaceChats itself (no layout shift).
  container.style.display = "contents";
  return container;
}

/**
 * Hidden parking node used while the Chats route is not on screen. Visually hidden, inert and
 * aria-hidden: the parked chat UI can neither be seen, focused nor clicked. Call UI that must
 * stay usable (full call overlay, floating call window) is portalled to <body> separately.
 */
function getParking() {
  if (parking || typeof document === "undefined") return parking;
  parking = document.createElement("div");
  parking.setAttribute("data-persistent-chats-parking", "");
  parking.setAttribute("aria-hidden", "true");
  parking.setAttribute("inert", "");
  Object.assign(parking.style, {
    position: "fixed",
    left: "0px",
    top: "0px",
    width: "0px",
    height: "0px",
    overflow: "hidden",
    visibility: "hidden",
    pointerEvents: "none",
  });
  document.body.appendChild(parking);
  return parking;
}

function placeContainer() {
  const c = getHostContainer();
  if (!c) return;
  const target = slotEl || getParking();
  if (target && c.parentNode !== target) target.appendChild(c);
}

/** Called by ChatsRouteSlot on mount. Returns the unregister function. */
export function registerChatsSlot(el) {
  if (!el) return () => {};
  slotEl = el;
  placeContainer();
  emit();
  return () => unregisterChatsSlot(el);
}

export function unregisterChatsSlot(el) {
  if (slotEl !== el) return; // a newer slot already took over
  slotEl = null;
  badgeValue = null;
  placeContainer(); // synchronous move → media elements are never left detached
  emit();
}

/** WorkspaceChats publishes whether a call/huddle is in progress (keep it mounted). */
export function setChatsKeepAlive(value) {
  const next = Boolean(value);
  if (keepAlive === next) return;
  keepAlive = next;
  emit();
}

/** The Chats route bridges the layout's CommunicationBadge context to the hosted instance. */
export function setChatsBadgeContext(value) {
  if (badgeValue === value) return;
  badgeValue = value || null;
  emit();
}

export function usePersistentChatsState() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function isChatsKeepAlive() {
  return keepAlive;
}

// Routes on which the floating call window must never be shown (unauthenticated screens and
// the full-screen meeting room). The call itself is unaffected; only the panel is hidden.
const SUPPRESSED_PREFIXES = [
  "/auth/",
  "/accept-invite",
  "/connect/",
  "/meet/",
  "/client-portal",
  "/vendor-portal",
  "/capture/",
  "/proposal/",
];

export function isCallWindowSuppressedPath(pathname = "") {
  const p = String(pathname || "");
  if (p === "/" || p === "/login") return true;
  return SUPPRESSED_PREFIXES.some((prefix) => p === prefix.replace(/\/$/, "") || p.startsWith(prefix));
}

// Context the host provides to the hosted Chat module.
//   hosted              — rendered by PersistentChatsHost (false in isolated renders/tests)
//   parked              — the Chats route is NOT on screen (chat UI hidden; call continues)
//   suppressCallWindow  — current route must not show the floating call window
//   navigateTo(to)      — router navigation without subscribing the 9k-line module to location
export const ChatsHostContext = createContext({
  hosted: false,
  parked: false,
  suppressCallWindow: false,
  navigateTo: (to) => {
    if (typeof window !== "undefined") window.location.assign(to);
  },
});

export function useChatsHost() {
  return useContext(ChatsHostContext);
}

/** Test-only: reset module state between tests. */
export function __resetPersistentChatsForTests() {
  slotEl = null;
  keepAlive = false;
  badgeValue = null;
  if (container?.parentNode) container.parentNode.removeChild(container);
  if (parking?.parentNode) parking.parentNode.removeChild(parking);
  container = null;
  parking = null;
  listeners.clear();
  snapshot = { slotEl: null, keepAlive: false, badgeValue: null };
}
